import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Check, X, Trash2, Star, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";

const TABS = [
  { id: "pending", label: "TE BEOORDELEN" },
  { id: "approved", label: "GEPUBLICEERD" },
  { id: "all", label: "ALLES" },
];

const StarRow = ({ rating }) => (
  <div className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={14}
        strokeWidth={1.5}
        fill={n <= rating ? "#ff1e1e" : "transparent"}
        color={n <= rating ? "#ff1e1e" : "#3f3f3f"}
      />
    ))}
  </div>
);

const AdminReviews = () => {
  const { authHeaders } = useOutletContext();
  const [tab, setTab] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/reviews`, { headers: authHeaders })
      .then((r) => setReviews(r.data || []))
      .catch(() => toast.error("Reviews laden mislukt"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  const filtered = useMemo(() => {
    if (tab === "pending") return reviews.filter((r) => !r.approved);
    if (tab === "approved") return reviews.filter((r) => r.approved);
    return reviews;
  }, [reviews, tab]);

  const counts = useMemo(
    () => ({
      pending: reviews.filter((r) => !r.approved).length,
      approved: reviews.filter((r) => r.approved).length,
      all: reviews.length,
    }),
    [reviews]
  );

  const setApproved = async (r, approved) => {
    setActingId(r.id);
    try {
      await axios.patch(
        `${API}/admin/reviews/${r.id}`,
        { approved },
        { headers: authHeaders }
      );
      toast.success(approved ? "Review gepubliceerd" : "Review verborgen");
      load();
    } catch {
      toast.error("Bijwerken mislukt");
    } finally {
      setActingId(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Review van ${r.display_name} permanent verwijderen?`)) return;
    setActingId(r.id);
    try {
      await axios.delete(`${API}/admin/reviews/${r.id}`, { headers: authHeaders });
      toast.success("Review verwijderd");
      load();
    } catch {
      toast.error("Verwijderen mislukt");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div data-testid="admin-reviews" className="space-y-8">
      <div>
        <div className="font-display text-3xl">REVIEWS</div>
        <div className="text-sm font-mono-x text-[var(--tha-muted)] mt-1">
          Modereer klantreviews voordat ze publiek worden. Alleen klanten met een
          afgeronde bestelling kunnen er één indienen.
        </div>
      </div>

      <div className="flex gap-px bg-[var(--tha-border)]">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`reviews-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 px-5 py-3 text-xs font-mono-x tracking-widest transition-colors ${
              tab === t.id
                ? "bg-[#111] text-white border-b-2 border-[var(--tha-red)]"
                : "bg-[#0a0a0a] text-[var(--tha-muted)] hover:text-white"
            }`}
          >
            {t.label}{" "}
            <span className="text-[var(--tha-red)]">({counts[t.id]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[var(--tha-muted)] font-mono-x text-sm">Laden…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--tha-border)] p-10 text-center">
          <MessageSquareQuote size={28} className="mx-auto text-[var(--tha-muted)] mb-3" />
          <div className="font-display text-2xl mb-1">GEEN REVIEWS</div>
          <div className="text-sm font-mono-x text-[var(--tha-muted)]">
            {tab === "pending"
              ? "Alle reviews zijn behandeld."
              : "Er zijn nog geen reviews in deze categorie."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const acting = actingId === r.id;
            return (
              <article
                key={r.id}
                data-testid={`review-row-${r.id}`}
                className="border border-[var(--tha-border)] bg-[#0a0a0a] p-5 flex flex-col sm:flex-row gap-5"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <StarRow rating={r.rating} />
                    <span className="font-display text-lg">{r.display_name}</span>
                    {r.approved ? (
                      <span className="text-[10px] font-mono-x px-2 py-0.5 border border-green-700 text-green-400">
                        GEPUBLICEERD
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono-x px-2 py-0.5 border border-[var(--tha-border)] text-[var(--tha-muted)]">
                        WACHT OP REVIEW
                      </span>
                    )}
                  </div>
                  {r.title && (
                    <h3 className="font-display text-xl mt-2 leading-tight">
                      {r.title}
                    </h3>
                  )}
                  <p className="mt-2 text-sm text-[var(--tha-muted)] whitespace-pre-line">
                    {r.body}
                  </p>
                  {r.photo_urls && r.photo_urls.length > 0 && (
                    <div className="mt-3 flex gap-2">
                      {r.photo_urls.map((u, i) => (
                        <a
                          key={i}
                          href={u}
                          target="_blank"
                          rel="noreferrer"
                          className="block w-20 h-20 border border-[var(--tha-border)] overflow-hidden hover:border-[var(--tha-red)]"
                        >
                          <img src={u} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-3 text-xs font-mono-x text-[var(--tha-muted)]">
                    <span>Producten: {(r.product_names || []).join(", ") || "—"}</span>
                    <span>·</span>
                    <span>{(r.created_at || "").slice(0, 10)}</span>
                    <span>·</span>
                    <span>{r.customer_email}</span>
                  </div>
                </div>
                <div className="flex sm:flex-col gap-2 sm:w-32 shrink-0">
                  {r.approved ? (
                    <button
                      data-testid={`review-unpublish-${r.id}`}
                      onClick={() => setApproved(r, false)}
                      disabled={acting}
                      className="flex-1 border border-[var(--tha-border)] hover:border-white text-[var(--tha-muted)] hover:text-white text-xs font-mono-x tracking-widest px-3 py-2 inline-flex items-center justify-center gap-2 disabled:opacity-40"
                    >
                      <X size={12} /> VERBERG
                    </button>
                  ) : (
                    <button
                      data-testid={`review-approve-${r.id}`}
                      onClick={() => setApproved(r, true)}
                      disabled={acting}
                      className="flex-1 bg-[var(--tha-red)] text-black text-xs font-mono-x tracking-widest px-3 py-2 inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-40"
                    >
                      <Check size={12} /> GOEDKEUR
                    </button>
                  )}
                  <button
                    data-testid={`review-delete-${r.id}`}
                    onClick={() => remove(r)}
                    disabled={acting}
                    className="flex-1 border border-[var(--tha-border)] text-[var(--tha-muted)] hover:border-red-700 hover:text-red-500 text-xs font-mono-x tracking-widest px-3 py-2 inline-flex items-center justify-center gap-2 disabled:opacity-40"
                  >
                    <Trash2 size={12} /> WIS
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
